import { ConfirmCancelButtons } from "@components/Buttons/ConfirmCancelButtons";
import LoadingIcon from "@common/LoadingIcon";
import WarningModal from "@components/Modals/WarningModal";
import messages from "@constants/Messages";
import { TenantBasicInfoCard } from "@components/Forms/common/components/tenant/TenantBasicInfoCard";
import { ContractStatusCard } from "@components/Forms/common/components/tenant/ContractStatusCard";
import { ShoppersCloudSettingsCard } from "@components/Forms/common/components/tenant/ShoppersCloudSettingsCard";
import { SecuritySettingsCard } from "@components/Forms/common/components/tenant/SecuritySettingsCard";
import { useViewTenantForm } from "@components/Forms/ViewTenantForm/hooks/useViewTenantForm";
import { useEffect, useState } from "react";
import { showAvailableKeycloakGroups } from "../../../services/API/Requests/TenantRequests";
import { PermissionsCard } from "./components/PermissionsCard";
import { useSelector } from "react-redux";

interface TenantInformationProps {
  tenantCode: string | undefined;
}

const ViewTenantForm: React.FC<TenantInformationProps> = ({ tenantCode }) => {
  const form = useViewTenantForm(tenantCode);
  const [keycloakGroups, setKeycloakGroups] = useState([]);
  const isAdminLoggedIn: boolean = useSelector(
    (state: any) => state.auth.isAdmin,
  );

  const fetchKeycloakGroups = async () => {
    const groups = await showAvailableKeycloakGroups(
      tenantCode || "",
      form.setLoading,
      form.setError,
    );
    if (groups) {
      setKeycloakGroups(groups);
    }
  };

  useEffect(() => {
    fetchKeycloakGroups();
  }, []);

  return (
    <div>
      {form.loading ? (
        <div className="flex justify-center items-center min-h-screen">
          <LoadingIcon />
        </div>
      ) : form.tenantData?.id ? (
        <>
          <div className="flex flex-row justify-end">
            {form.error && (
              <button
                className="button-default"
                type="button"
                onClick={form.handleOpenWarningModal}
              >
                {messages.generic.show_error}
              </button>
            )}
          </div>
          <form
            onSubmit={(e) => form.onSubmit(e, isAdminLoggedIn)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
          >
            <ContractStatusCard
              contractData={form.contractData}
              setContractData={form.setContractData}
              updateContractData={form.updateContractData}
              appData={form.appData}
              loading={form.loading}
              readOnly={isAdminLoggedIn ? false : true}
              tenantCode={tenantCode}
            />

            <TenantBasicInfoCard
              tenantData={form.tenantData}
              updateTenantData={form.updateTenantData}
              errors={form.errors}
              isAdminLoggedIn={form.isAdminLoggedIn}
              updateError={form.updateError}
              loading={form.loading}
            />

            <PermissionsCard
              keycloakGroups={keycloakGroups.available_keycloak_groups}
              tenantData={form.tenantData}
              updateTenantData={form.updateTenantData}
              isAdminLoggedIn={form.isAdminLoggedIn}
              loading={form.loadingKeycloakGroups}
              handleCheckboxChange={form.handleKeycloakGroupsCheckboxChange}
            />

            {form.useHazumApi &&
              form.hasYobelyLicense &&
              form.isAdminLoggedIn && (
                <ShoppersCloudSettingsCard
                  tenantData={form.tenantData}
                  updateTenantData={form.updateTenantData}
                  errors={form.errors}
                  updateError={form.updateError}
                  loading={form.loading}
                />
              )}

            <SecuritySettingsCard
              tenantData={form.tenantData}
              updateTenantData={form.updateTenantData}
              loading={form.loading}
            />

            <ConfirmCancelButtons
              confirmText={"更新する"}
              form={true}
              onCancel={form.onCancel}
            />
          </form>
          <WarningModal
            title={messages.generic.error_modal_title}
            body={form.error}
            confirmText={""}
            cancelText="戻る"
            isOpen={form.warningModalOpen}
            onRequestClose={form.handleCloseWarningModal}
          ></WarningModal>
        </>
      ) : (
        <p className="not-found-text">{`テナント: ${form.id}のデータが見つかりませんでした`}</p>
      )}
    </div>
  );
};

export default ViewTenantForm;
